import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import sortBy from 'lodash-es/sortBy';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, first, map, take, withLatestFrom } from 'rxjs/operators';
import { AccountsService } from '../../accounts/services/accounts.service';
import { EntityState, WINDOW, WindowWrapper } from '../../common/index';
import {
    Application, ApplicationActiveState, ApplicationGroupTile, ApplicationUrlType
} from '../models/index';
import {
    applicationsCreateAction, applicationsDeleteAction, applicationsSetActiveAction,
    applicationsUpdateAction, getApplicationsActiveApplication, getApplicationsActiveIdx,
    getApplicationsActiveState, getApplicationsData, getApplicationsError,
    getApplicationsGroups, getApplicationsState, getApplicationsToggling
} from '../store/applications.store';
import { ApplicationTile } from './../models/Application';
import { GaApplicationsConfigService } from './applications.config.service';

@Injectable()
export class GaApplicationsService {

    constructor(
        private applicationsConfig: GaApplicationsConfigService,
        private router: Router,
        private store: Store<any>,
        private accountsService: AccountsService,
        private translate: TranslateService,
        @Inject(WINDOW) private window: WindowWrapper
    ) { }

    public get applications$(): Observable<ApplicationTile[]> {
        return combineLatest([
            this.store.select(getApplicationsData).pipe(
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
            ),
            this.accountsService.preferenceByKey$('favorites'),
        ]).pipe(
            map(([apps, favorites]: [ApplicationTile[], string[]]) =>
             apps.map(app => ({...app, isFavorite: favorites && favorites.includes(app.id)})))
        );
    }

    public get activeApplicationIdx$(): Observable<number> {
        return this.store.select(getApplicationsActiveIdx);
    }

    public get activeApplicationState$(): Observable<ApplicationActiveState> {
        return this.store.select(getApplicationsActiveState);
    }

    public get activeApplication$(): Observable<ApplicationTile> {
        return this.store.select(getApplicationsActiveApplication);
    }

    public get groups$(): Observable<ApplicationGroupTile[]> {
        return combineLatest([
            this.store.select(getApplicationsGroups).pipe(
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
            ),
            this.applications$
        ]).pipe(
            map(([groups, apps]: [ApplicationGroupTile[], ApplicationTile[]]) => {
                const groupsIdx = {};
                for (const [k, v] of Object.entries(groups)) {
                    groupsIdx[v.id] = k;
                    groups[k].applications = [];
                }
                const groupsIds = Object.keys(groupsIdx);
                for (const app of apps) {
                    if (!app.group || !groupsIds.includes(app.group)) { continue; }
                    const idx = groupsIdx[app.group];
                    groups[idx].applications.push(app);
                }
                groups = sortBy(groups, ['id']);
                for (const g of groups) {
                    g.applications = sortBy(g.applications, ['id']);
                }
                return groups;
            })
        );
    }

    public get favorites$(): Observable<ApplicationTile[]> {
        return combineLatest([
            this.applications$,
            this.accountsService.preferenceByKey$('favorites')
        ]).pipe(
            map(([apps, favorites]: [ApplicationTile[], string[]]) => {
                favorites ??= [];
                if (!favorites.length) { favorites.push(...this.applicationsConfig.config.defaultFavoritesIds); }
                return apps.filter(app => favorites.includes(app.id)).sort((a, b) => {
                    const aid = favorites.indexOf(a.id);
                    const bid = favorites.indexOf(b.id);
                    return aid < bid ? -1 : (aid > bid ? 1 : 0);
                });
            })
        );
    }

    public get favoritesGroup$(): Observable<ApplicationGroupTile> {
        return this.favorites$.pipe(
            map(favs => ({
                id: this.applicationsConfig.favoritesGroupName,
                display_name: new Proxy({}, {
                    get: (target, name) => this.translate.instant(this.applicationsConfig.favoritesGroupName)
                }),
                applications: favs,
                icon: 'star_rate',
                iconView: {
                    fontIcon: 'star_rate'
                },
                params: {
                    useComponentName: true
                }
            } as ApplicationGroupTile))
        );
    }

    public get state$(): Observable<EntityState> {
        return this.store.select(getApplicationsState);
    }

    public get toggling$(): Observable<boolean> {
        return this.store.select(getApplicationsToggling);
    }

    public get error$(): Observable<any> {
        return this.store.select(getApplicationsError);
    }

    /**
     * Check is another collor has bigger priority than base one
     * @param base base color
     * @param another another color
     * @returns `true` if another color has bigger priority or `false` if not
     */
    // public isColorUpper(base: string, another: string): boolean {
    //     return !base || base === '' ||
    //         (base !== another &&
    //             this._colorHeatmap.hasOwnProperty(base) &&
    //             this._colorHeatmap.hasOwnProperty(another) &&
    //             this._colorHeatmap[base] < this._colorHeatmap[another]);
    // }

    /**
     * Find application by identity
     * @param id Application identity
     * @param exact If `true` will find exact match of identity, if `false` - regex match
     * @returns found application or `null`
     */
    public findByid(id: string, exact?: boolean): ApplicationTile {
        let app: ApplicationTile;
        this.applications$.pipe(take(1)).subscribe(apps => {
            if (!apps || !apps.length) { return; }
            app = apps.find(a => exact ? a.id === id : !!a.id.match(id)) || null;
        });
        return app;
    }

    /**
     * Find application by name
     * @param name Application name
     * @param exact If `true` will find exact match of name, if `false` - regex match
     * @returns found application or `null`
     */
    public findByName(name: string, exact?: boolean): ApplicationTile {
        let app: ApplicationTile;
        this.applications$.pipe(take(1)).subscribe(apps => {
            if (!apps || !apps.length) { return; }
            app = apps.find(a => Object.keys(a.name).some(k => exact ? a.name[k] === name : a.name[k].indexOf(name) !== -1)) || null;
        });
        return app;
    }

    /**
     * Toggle application favorite flag
     * @param app Application which should be toggled
     * @param state New application state. If not defined will be used state inverted to current
     */
    public toggleFavorite(app: ApplicationTile, state?: boolean): void {
        this.accountsService.preferenceByKey$('favorites').pipe(
            withLatestFrom(this.applications$),
            first()
        ).subscribe(([favorites, apps]: [string[], ApplicationTile[]]) => {
            const found = apps.find(a => a.id === app.id);
            if (found) {
                const newState = typeof state === 'undefined' ? !app.isFavorite : state;
                const key = this.applicationsConfig.favoritesGroupName;
                const inFavoritesIdx = favorites.indexOf(app.id);
                const inFavorites = inFavoritesIdx >= 0;
                if (inFavorites !== newState) {
                    if (inFavorites) {
                        // remove from favorites
                        favorites.splice(inFavoritesIdx, 1);
                    } else {
                        // add into favorites
                        favorites.push(app.id);
                    }
                    this.accountsService.setPreferenceByKey(key, favorites);
                }
            }
        });
    }

    public setActiveApplication(app: ApplicationTile): void {
        this.applications$.pipe(
            first()
        ).subscribe(apps => {
            this.store.dispatch(applicationsSetActiveAction({ id: app.id }));
        });
    }

    /**
     * Create new application
     * @param application Application to create
     */
    public create(application: Application): void {
        this.store.dispatch(applicationsCreateAction({ application }));
    }

    /**
     * Update application by identity
     * @param id Application identity which will be updated
     * @param application Application to update
     */
    public update(id: string, application: Application): void {
        this.store.dispatch(applicationsUpdateAction({ id, application }));
    }

    /**
     * Delete applciation by identity
     * @param id Application identity
     */
    public delete(id: string): void {
        this.store.dispatch(applicationsDeleteAction({ id }));
    }

    /**
     * Navigate to any listed application
     * @param id Application or ApplicationTile or Application id to navigate to
     * @param params Extra params to modify application to navigate to
     * @param params.state Navigate to different state of application
     * @param params.state_params Navigate to different state params
     */
    public navigateToApplication(id: string | Application | ApplicationTile, params?: {
        state?: string,
        state_params?: { [key: string]: string }
    }): void {
        this.applications$.pipe(
            first(),
            withLatestFrom(this.activeApplication$)
        ).subscribe(([apps, activeApplication]) => {
            const app = typeof id === 'string' ? apps.find(a => !!a.id.match(id)) : id as ApplicationTile;
            const resolvedParams = params || {};
            if (app && app.url) {
                if (app.url.type === ApplicationUrlType.Outer) {
                    const openSelf = app.url.params && app.url.params.target || '_self';
                    this.window.open(app.url.link, openSelf);
                } else if (app.url.type === ApplicationUrlType.Inner) {
                    resolvedParams.state = resolvedParams.state || app.url.state;
                    if (app.url.state_params) {
                        resolvedParams.state_params = Object.assign({}, app.url.state_params, resolvedParams.state_params);
                    }
                    if (activeApplication) {
                        if (activeApplication.id === app.id || activeApplication.url.link === app.url.link) {
                            // same app, other state
                            this.setActiveApplication(app);

                            if (resolvedParams && resolvedParams.state) {
                                this.router.navigate([resolvedParams.state], resolvedParams.state_params);
                            } else {
                                this.router.navigateByUrl('/');
                            }

                            return;
                        }
                    }
                    const url = '/' + (app.url.link.startsWith('/') ? app.url.link.slice(1) : app.url.link);
                    this.window.sessionStorage.setItem('appChangeParams', JSON.stringify(resolvedParams));
                    this.window.sessionStorage.setItem('activeApplicationId', app.id);
                    this.window.location.href = window.location.origin + url;
                }
            }
        });
    }
}
